import { FC, useCallback, useState } from 'react'
import { Helmet } from 'react-helmet'
import { cn } from '@bem-react/classname'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { Memo } from '@/hoc/Memo'
import yup from '@/utils/yup-extended'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import useBackendError from '@/hooks/useBackendError'
import { AddLicenseModel, LicenseCreationModes } from './models/AddLicenseModel'
import { getDeviceSerialThunk } from '@/core/services/LicenseVersions/store/license-versions.slice'
import { setItem } from '@/utils/persistanceStorage'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'
import { roleSelector } from '../AccountPage/store/accont.selector'

import { Back } from '@/components/Back'
import { Button } from '@/components/Button'
import { FormGroup } from '@/components/form/FormGroup'
import { Field } from '@/components/form/Field'

const cnCreateLicense = cn('CreateLicense')

import './styles/CreateLicense.scss'
import { RoleUserModel } from '@/core/services/Token/model/TokenModel'
import { paths } from '@/config'
import { FormControlLabel, Radio, RadioGroup } from '@mui/material'

export const CreateLicense: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [setBackendError] = useBackendError()
  const role = useAppSelector(roleSelector)

  const [licenseCreationMode, setLicenseCreationMode] =
    useState<LicenseCreationModes>(LicenseCreationModes.ManualSelect)

  const SerialSchema = yup.object().shape({
    serial: yup.string().trim().required(t('This field is required')),
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AddLicenseModel>({
    mode: 'onChange',
    resolver: yupResolver(SerialSchema),
  })

  const createSerialNumber = useCallback(
    async (serial: string) => {
      try {
        dispatch(changeLoading(true))
        const res = await dispatch(getDeviceSerialThunk(serial)).unwrap()
        dispatch(changeLoading(false))
        const isOnly = res.length === 1
        const isAlreadyActive = res[0].isAlreadyActive

        if (isOnly && isAlreadyActive && role !== RoleUserModel.Reseller) {
          navigate(
            `/cabinet/licenses/renew?licenseId=${res[0].latestLicenseId}`,
          )
        } else {
          navigate(`${paths.PRODUCTS_SELECT_PAGE}?serialNumber=${serial}`)
          setItem('form', { serialNumber: serial })
        }
      } catch (errors) {
        setBackendError(errors)
      } finally {
        dispatch(changeLoading(false))
      }
    },
    [dispatch],
  )

  const createLicense = useCallback(async ({ serial }: AddLicenseModel) => {
    await createSerialNumber(serial)
  }, [])

  const handleSkipSerialNumber = () => {
    navigate(paths.PRODUCTS_SELECT_PAGE)
  }

  const handleIsSkipSerialNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLicenseCreationMode(Number(event.target.value))
  }

  return (
    <>
      <Helmet>
        <title>{t('Add new License')} | Registration</title>
      </Helmet>
      <div className={cnCreateLicense()}>
        <Back />
        <div className={cnCreateLicense('wrap')}>
          <div className={cnCreateLicense('block')}>
            <div className={cnCreateLicense('text')}>
              {t('Do you know, which DTM license type do you need?')}
            </div>

            <div className={cnCreateLicense('checkboxes')}>
              <RadioGroup
                value={licenseCreationMode}
                onChange={handleIsSkipSerialNumberChange}
              >
                <FormControlLabel
                  value={LicenseCreationModes.ManualSelect}
                  label={t('Yes, I know the license type.')}
                  control={<Radio />}
                />
                <FormControlLabel
                  value={LicenseCreationModes.ByDeviceSerialNumber}
                  label={t(`No, I don't. But I know the device Serial Number.`)}
                  control={<Radio />}
                />
              </RadioGroup>
            </div>

            <div className={cnCreateLicense('content')}>
              {licenseCreationMode === LicenseCreationModes.ManualSelect && (
                <div className={cnCreateLicense('btn')}>
                  <Button
                    className={cnCreateLicense('button')}
                    type='button'
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    onClick={handleSkipSerialNumber}
                  >
                    {t('Next')}
                  </Button>
                </div>
              )}

              {licenseCreationMode ===
                LicenseCreationModes.ByDeviceSerialNumber && (
                <form onSubmit={handleSubmit(createLicense)}>
                  <FormGroup>
                    <Field
                      {...register('serial')}
                      placeholder={t('Serial Number')}
                      label={t('Serial Number')}
                      required
                      errors={errors.serial && errors.serial.message}
                    />
                  </FormGroup>

                  <div className={cnCreateLicense('btn')}>
                    <Button
                      className={cnCreateLicense('button')}
                      type='submit'
                      isLoading={isSubmitting}
                      isDisabled={isSubmitting}
                    >
                      {t('Submit')}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
          <div className={cnCreateLicense('example')}>
            <img
              className={cnCreateLicense('img')}
              src='/img/create-serial.jpg'
            />
          </div>
        </div>
      </div>
    </>
  )
})
